import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, MessageCircle, Sparkles, TrendingUp, DollarSign, RefreshCw, Crown, Zap, ArrowLeft, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const { data: res } = await axios.get(`${API_URL}/api/admin/dashboard`, { withCredentials: true });
      setData(res);
      setRefreshedAt(new Date());
      setError(null);
    } catch (err) {
      console.error('Admin stats fetch failed:', err);
      setError(err?.response?.status === 403 ? 'forbidden' : 'network');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) fetchStats();
  }, [authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Access guard
  if (!authLoading && !user) {
    navigate('/login');
    return null;
  }

  if (error === 'forbidden') {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-[#E11D48] mb-4" />
        <h2 className="text-2xl font-black text-white mb-2 font-['Unbounded']">Access denied</h2>
        <p className="text-white/60 mb-6">This page is owner-only.</p>
        <button
          onClick={() => navigate('/')}
          className="px-6 py-2.5 rounded-full bg-white text-black font-bold text-sm"
          data-testid="admin-go-home"
        >
          Back to app
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white pb-20" data-testid="admin-dashboard">
      {/* Top bar */}
      <div
        className="sticky top-0 z-10 bg-[#050505]/90 backdrop-blur-md border-b border-white/5 px-4 pb-3 flex items-center gap-3"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <button
          onClick={() => navigate('/profile')}
          className="p-2 -ml-2 text-white/70 hover:text-white rounded-full hover:bg-white/5"
          data-testid="admin-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-black text-lg text-white tracking-tight font-['Unbounded']">Admin Dashboard</h1>
          {refreshedAt && (
            <p className="text-[10px] text-white/40">Updated {refreshedAt.toLocaleTimeString()}</p>
          )}
        </div>
        <button
          onClick={fetchStats}
          disabled={loading}
          className="p-2 text-white/70 hover:text-white rounded-full hover:bg-white/5 disabled:opacity-40"
          data-testid="admin-refresh-btn"
          aria-label="Refresh stats"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading && !data && (
        <div className="px-4 py-20 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#E11D48] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {error === 'network' && !data && (
        <div className="px-4 py-20 text-center">
          <p className="text-[#E11D48] font-medium">Couldn&apos;t load stats. Tap refresh.</p>
        </div>
      )}

      {data && (
        <div className="px-4 pt-4 space-y-4">
          {/* USERS BLOCK */}
          <Section icon={<Users className="w-4 h-4" />} title="Users" accent="text-cyan-400">
            <Big label="Total signups" value={data.users.total} testid="stat-total-users" />
            <Row>
              <Mini label="Today" value={data.users.today} highlight />
              <Mini label="7 days" value={data.users.week} />
              <Mini label="30 days" value={data.users.month} />
            </Row>
            <Row>
              <Mini label="Active 24h" value={data.users.active_24h} accent="text-emerald-400" />
              <Mini label="Premium" value={data.users.premium} accent="text-[#F59E0B]" icon={<Crown className="w-3 h-3" />} />
              <Mini label="Ultimate" value={data.users.ultimate} accent="text-[#E11D48]" icon={<Zap className="w-3 h-3" />} />
            </Row>
          </Section>

          {/* REVENUE BLOCK */}
          <Section icon={<DollarSign className="w-4 h-4" />} title="Revenue" accent="text-emerald-400">
            <Big label="Lifetime revenue" value={`£${data.revenue.total.toLocaleString()}`} testid="stat-revenue-total" />
            <Row>
              <Mini label="Today" value={`£${data.revenue.today.toLocaleString()}`} highlight />
              <Mini label="Transactions" value={data.revenue.paid_transactions} />
              <Mini label="ARPU" value={data.users.total > 0 ? `£${(data.revenue.total / data.users.total).toFixed(2)}` : '—'} accent="text-cyan-400" />
            </Row>
          </Section>

          {/* ENGAGEMENT BLOCK */}
          <Section icon={<MessageCircle className="w-4 h-4" />} title="Engagement" accent="text-fuchsia-400">
            <Big label="Total messages" value={data.engagement.total_messages.toLocaleString()} testid="stat-total-messages" />
            <Row>
              <Mini label="Last 24h" value={data.engagement.messages_24h.toLocaleString()} highlight />
              <Mini label="Conversations" value={data.engagement.total_conversations.toLocaleString()} />
              <Mini label="Msgs/User" value={data.users.total > 0 ? (data.engagement.total_messages / data.users.total).toFixed(1) : '—'} />
            </Row>
          </Section>

          {/* CHARACTERS BLOCK */}
          <Section icon={<Sparkles className="w-4 h-4" />} title="Characters" accent="text-orange-400">
            <Row>
              <Mini label="Total" value={data.characters.total} highlight />
              <Mini label="Public" value={data.characters.public} accent="text-emerald-400" />
              <Mini label="Flagged" value={data.characters.flagged} accent={data.characters.flagged > 0 ? 'text-[#E11D48]' : 'text-white/60'} />
            </Row>
          </Section>

          {/* TOP CHARACTERS */}
          {data.top_characters && data.top_characters.length > 0 && (
            <Section icon={<TrendingUp className="w-4 h-4" />} title="Top Characters by Chats" accent="text-pink-400">
              <div className="space-y-2 pt-2">
                {data.top_characters.map((ch, i) => (
                  <div key={ch.id} className="flex items-center gap-3 p-2 rounded-lg bg-white/[0.02] border border-white/5" data-testid={`top-char-${i}`}>
                    <span className="text-lg font-black text-white/30 font-['Unbounded'] w-6 text-center">{i + 1}</span>
                    {ch.avatar_url ? (
                      <img src={ch.avatar_url} alt={ch.name} className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-white/5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{ch.name}</p>
                      <p className="text-[11px] text-white/50">{ch.chats.toLocaleString()} chats</p>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          <p className="text-center text-[10px] text-white/30 pt-2">Owner-only · pull-to-refresh enabled</p>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────

function Section({ icon, title, accent = 'text-white/60', children }) {
  return (
    <section className="bg-[#1A1D24] rounded-2xl border border-white/5 overflow-hidden">
      <header className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-white/5">
        <span className={accent}>{icon}</span>
        <h2 className="text-xs font-bold tracking-widest uppercase text-white/80">{title}</h2>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Big({ label, value, testid }) {
  return (
    <div className="mb-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider text-white/40 font-bold mb-1">{label}</p>
      <p className="text-3xl font-black text-white font-['Unbounded'] leading-none">{value}</p>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-3 gap-2">{children}</div>;
}

function Mini({ label, value, highlight = false, accent = 'text-white', icon = null }) {
  return (
    <div className={`px-2.5 py-2 rounded-lg ${highlight ? 'bg-[#E11D48]/10 border border-[#E11D48]/20' : 'bg-white/[0.03] border border-white/5'}`}>
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-white/50 font-bold mb-0.5">
        {icon}
        {label}
      </div>
      <p className={`text-lg font-black ${accent} font-['Unbounded'] leading-none`}>{value}</p>
    </div>
  );
}
